import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Plus } from '@phosphor-icons/react';
import { FAQ } from '../../data/site';
import Container from '../ui/Container';
import Reveal, { EASE } from '../ui/Reveal';

function FaqItem({ item, index, open, onToggle }) {
  const reduce = useReducedMotion();
  const panelId = `faq-panel-${index}`;
  const buttonId = `faq-button-${index}`;

  return (
    <li className="border-b border-line">
      <h3>
        <button
          type="button"
          id={buttonId}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={onToggle}
          className="flex w-full items-center justify-between gap-6 py-5 text-left transition-colors hover:text-accent sm:py-6"
        >
          <span className="text-[17px] font-semibold leading-snug tracking-tight sm:text-lg">{item.question}</span>
          <motion.span
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-line bg-surface text-ink"
            animate={{ rotate: open ? 45 : 0 }}
            transition={reduce ? { duration: 0 } : { duration: 0.35, ease: EASE }}
            aria-hidden="true"
          >
            <Plus size={16} weight="bold" />
          </motion.span>
        </button>
      </h3>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            id={panelId}
            role="region"
            aria-labelledby={buttonId}
            key="content"
            initial={reduce ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={reduce ? { height: 0, opacity: 0, transition: { duration: 0 } } : { height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden"
          >
            <p className="max-w-2xl pb-6 text-[15px] leading-[1.8] text-ink-soft sm:text-base">{item.answer}</p>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </li>
  );
}

function Faq() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className="py-20 sm:py-28">
      <Container>
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-4">
            <h2 className="text-[1.85rem] font-bold leading-[1.25] tracking-[-0.02em] sm:text-4xl lg:text-[2.6rem]">
              자주 묻는 질문
            </h2>
            <p className="mt-4 text-base leading-[1.75] text-ink-soft sm:text-lg">
              여기 없는 내용은 문의로 남겨 주세요. 확인 후 답변드립니다.
            </p>
            <Link
              to="/contact"
              className="group mt-6 inline-flex items-center gap-1.5 text-[15px] font-semibold text-accent"
            >
              문의하러 가기
              <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </Link>
          </Reveal>

          <Reveal as="ul" className="border-t border-line lg:col-span-8" delay={0.1}>
            {FAQ.map((item, index) => (
              <FaqItem
                key={item.question}
                item={item}
                index={index}
                open={openIndex === index}
                onToggle={() => setOpenIndex((prev) => (prev === index ? -1 : index))}
              />
            ))}
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

export default Faq;
